/**
 * useChatRateLimit Hook
 * Manages rate limit state, countdown timer, and progressive warning detection
 * Following enterprise-grade patterns with real-time updates
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { RateLimit } from '../types/chat.types';

/**
 * Rate limit color state
 */
export type RateLimitColor = 'green' | 'yellow' | 'orange' | 'red';

/**
 * Progressive warning level
 */
export type WarningLevel = 'none' | 'low' | 'medium' | 'high' | 'critical';

/**
 * Rate limit state with computed values
 */
export interface RateLimitState {
  limit: number;
  remaining: number;
  resetAt: Date | null;
  burstRemaining: number;
  percentage: number;
  color: RateLimitColor;
  warningLevel: WarningLevel;
  secondsUntilReset: number | null;
  isRateLimited: boolean;
  shouldShowWarning: boolean;
}

/**
 * Hook return type
 */
export interface UseChatRateLimitReturn {
  rateLimitState: RateLimitState | null;
  updateRateLimit: (rateLimit: RateLimit | null) => void;
  getColor: (remaining: number, limit: number) => RateLimitColor;
  getWarningLevel: (remaining: number, limit: number) => WarningLevel;
  formatCountdown: (seconds: number) => string;
}

/**
 * Get color based on remaining percentage
 */
const getRateLimitColor = (remaining: number, limit: number): RateLimitColor => {
  if (limit === 0) return 'red';
  const percentage = (remaining / limit) * 100;
  if (percentage > 50) return 'green';
  if (percentage > 25) return 'yellow';
  if (percentage > 0) return 'orange';
  return 'red';
};

/**
 * Get warning level based on remaining messages
 */
const getWarningLevel = (remaining: number, limit: number): WarningLevel => {
  if (limit === 0) return 'critical';
  
  if (remaining === 0) return 'critical';
  if (remaining === 1) return 'high';
  if (remaining <= 3) return 'medium';
  if (remaining <= 5) return 'low';
  return 'none';
};

/**
 * Format countdown seconds to human-readable string
 */
const formatCountdown = (seconds: number): string => {
  if (seconds <= 0) return '0s';
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes > 0) {
    return remainingSeconds > 0 
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }
  
  return `${remainingSeconds}s`;
};

/**
 * Calculate seconds until reset
 */
const calculateSecondsUntilReset = (resetAt: Date | null): number | null => {
  if (!resetAt) return null;
  
  const now = Date.now();
  const resetTime = resetAt.getTime();
  const diff = Math.max(0, Math.ceil((resetTime - now) / 1000));
  
  return diff;
};

/**
 * useChatRateLimit Hook
 * Manages rate limit state with real-time countdown updates
 */
export function useChatRateLimit(): UseChatRateLimitReturn {
  const [rateLimit, setRateLimit] = useState<RateLimit | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);

  /**
   * Update rate limit state
   */
  const updateRateLimit = useCallback((newRateLimit: RateLimit | null) => {
    setRateLimit(newRateLimit);
    
    // Clear existing interval
    if (countdownIntervalRef.current) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    
    // Start countdown if rate limit exists
    if (newRateLimit && newRateLimit.resetAt) {
      const updateCountdown = () => {
        const seconds = calculateSecondsUntilReset(newRateLimit.resetAt);
        setCountdown(seconds);
        
        // Clear interval if countdown reached zero
        if (seconds === null || seconds <= 0) {
          if (countdownIntervalRef.current) {
            window.clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
        }
      };
      
      // Initial update
      updateCountdown();
      
      // Update every second
      countdownIntervalRef.current = window.setInterval(updateCountdown, 1000);
    } else {
      setCountdown(null);
    }
  }, []);

  /**
   * Cleanup interval on unmount
   */
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        window.clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, []);

  /**
   * Compute rate limit state
   */
  const rateLimitState: RateLimitState | null = rateLimit ? {
    limit: rateLimit.limit,
    remaining: rateLimit.remaining,
    resetAt: rateLimit.resetAt,
    burstRemaining: rateLimit.burstRemaining,
    percentage: rateLimit.limit > 0 ? (rateLimit.remaining / rateLimit.limit) * 100 : 0,
    color: getRateLimitColor(rateLimit.remaining, rateLimit.limit),
    warningLevel: getWarningLevel(rateLimit.remaining, rateLimit.limit),
    secondsUntilReset: countdown,
    isRateLimited: rateLimit.remaining === 0,
    shouldShowWarning: rateLimit.remaining <= 5 && rateLimit.remaining > 0,
  } : null;

  return {
    rateLimitState,
    updateRateLimit,
    getColor: getRateLimitColor,
    getWarningLevel: getWarningLevel,
    formatCountdown,
  };
}

